/**
 * Utility to show alerts to users
 * Use this in .catch blocks instead of console.error
 */

export interface AlertData {
    id: string;
    heading: string;
    message: string;
    variant: string;
    count: number;
}

let alertsState: AlertData[] = [];
let listeners: Array<(alerts: AlertData[]) => void> = [];

function normalizeErrorMessage(text: string): string {
    // Remove instance IDs and other numeric IDs from error messages
    // Change "instance" to "instances" (plural) when grouping multiple instances
    // Order matters: more specific patterns first
    return text
        .replace(/\s+for\s+instance\s+\d+/gi, ' for instances')
        .replace(/\s+of\s+instance\s+\d+/gi, ' of instances')
        .replace(/\s+with\s+id\s+\d+/gi, '')
        .replace(/\s+instance\s+\d+/gi, ' instances')
        .replace(/\s+id\s+\d+/gi, '');
}

/**
 * Build a user-facing error message from an unknown error value.
 * Centralises extraction of Axios response bodies (error.response.data),
 * JSON error payloads (error.response.data.message), and standard Error.message.
 */
export function getErrorMessage(error: any): string {
    // If it's already a string, just use it as-is.
    if (typeof error === 'string') {
        return error;
    }

    // Handle Axios-style errors with response data.
    const responseData = error?.response?.data;

    // If backend returned plain text as body, prefer that.
    if (typeof responseData === 'string') {
        return responseData;
    }

    // If backend returned a JSON object with a message field, prefer that.
    if (responseData && typeof responseData.message === 'string') {
        return responseData.message;
    }

    // Fall back to Error.message when available.
    if (error && typeof error.message === 'string') {
        return error.message;
    }

    // Final fallback to toString().
    if (error && typeof error.toString === 'function') {
        return error.toString();
    }

    return 'An unknown error occurred';
}

/**
 * Show an error alert - use this in .catch blocks
 * Automatically groups identical errors and shows count (e.g., "Error (3)")
 * Groups errors with similar patterns (e.g., different instance IDs) together
 */
export function showError(heading: string, message: string) {
    // Normalize heading and message for comparison
    const normalizedHeading = normalizeErrorMessage(heading);
    const normalizedMessage = normalizeErrorMessage(message);
    
    // Check if this error already exists in the current alerts
    const existingAlertIndex = alertsState.findIndex(
        a => a.heading === normalizedHeading && a.message === normalizedMessage
    );
    
    if (existingAlertIndex !== -1) {
        // Increment count on existing alert
        alertsState[existingAlertIndex] = {
            ...alertsState[existingAlertIndex],
            count: alertsState[existingAlertIndex].count + 1
        };
        notifyListeners();
        return;
    }
    
    // Create and show the alert with normalized (ID-free) messages
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const alert: AlertData = { id, heading: normalizedHeading, message: normalizedMessage, variant: 'alert-danger', count: 1 };
    
    alertsState.push(alert);
    notifyListeners();
}

export function removeAlert(id: string) {
    alertsState = alertsState.filter(a => a.id !== id);
    notifyListeners();
}

export function subscribe(listener: (alerts: AlertData[]) => void) {
    listeners.push(listener);
    return () => {
        listeners = listeners.filter(l => l !== listener);
    };
}

function notifyListeners() {
    listeners.forEach(listener => listener([...alertsState]));
}
